import { Button } from '@mantine/core';
import { ChevronsDown, ChevronsUp } from 'lucide-react';
import { FC, ReactElement, useMemo, useState } from 'react';
import useSWR from 'swr';

import CircularLoader from '@/components/CircularLoader.tsx';
import DocumentCodePopup from '@/components/PatientModal/DocumentCodePopup.tsx';
import Paragraph from '@/components/text/Paragraph.tsx';

import usePrismic from '@/hooks/usePrismic.tsx';

import { PrismicDataType } from '@/models/PrismicHookTypes.ts';
import { DailyAppointment } from '@/models/appointment/Appointment.ts';
import { Document } from '@/models/documents/Document.ts';

import { getDocumentsByUser } from '@/services/documentsService.ts';

import { downloadDocument } from '@/utils/formUtils.ts';
import { filterPatientDocuments, getUnregisteredUserId } from '@/utils/patientUtils.ts';
import { getTextFromRichTextField } from '@/utils/prismicUtils.ts';

import DocumentItem from './DocumentItem.tsx';

interface Props {
    appointment: DailyAppointment;
    showDocumentsByAppointment?: boolean;
}

const DocumentExchange: FC<Props> = ({ appointment, showDocumentsByAppointment = true }) => {
    const { data: medical_records } = usePrismic(PrismicDataType.MEDICAL_RECORDS);
    const [showModal, setShowModal] = useState(false);
    const [documentToDownloadAfterCodeInput, setDocumentToDownloadAfterCodeInput] = useState<string | undefined>();
    const [displayCount, setDisplayCount] = useState(5);
    const userId = getUnregisteredUserId(appointment);

    const appointmentId = showDocumentsByAppointment ? appointment.id : undefined;

    const { data: documents, isLoading } = useSWR<Document[] | undefined>(
        ['appointment-documents', userId, appointmentId],
        () => getDocumentsByUser(userId, appointmentId)
    );

    const filteredDocuments = documents
        ? filterPatientDocuments(documents, showDocumentsByAppointment, appointment.id)
        : undefined;

    const visibleDocuments = useMemo(
        () => filteredDocuments?.slice(0, displayCount),
        [filteredDocuments, displayCount]
    );

    const hasMoreDocuments = filteredDocuments && filteredDocuments.length > displayCount;

    const toggleDisplayCount = (increase: boolean): void => {
        setDisplayCount((prev) => (increase ? prev + 5 : 5));
    };

    const download = async (documentId: string): Promise<void> => {
        await downloadDocument(appointment, documentId, () => {
            setShowModal(true);
            setDocumentToDownloadAfterCodeInput(documentId);
        });
    };

    const onInputCodeSuccess = async (): Promise<void> => {
        if (documentToDownloadAfterCodeInput) await download(documentToDownloadAfterCodeInput);
    };

    const showMoreLessButtons = (): ReactElement | undefined => {
        if (!hasMoreDocuments && displayCount <= 5) return undefined;

        return (
            <div className="flex items-center gap-2">
                {displayCount > 5 && (
                    <>
                        <Button
                            leftSection={<ChevronsUp className="size-4" />}
                            onClick={() => toggleDisplayCount(false)}
                            variant="transparent"
                            className="text-slate-500 hover:text-black"
                        >
                            Weniger anzeigen
                        </Button>
                        {hasMoreDocuments && <div className="h-6 w-px bg-slate-300" />}
                    </>
                )}
                {hasMoreDocuments && (
                    <Button
                        leftSection={<ChevronsDown className="size-4" />}
                        onClick={() => toggleDisplayCount(true)}
                        variant="transparent"
                        className="text-slate-500 hover:text-black"
                    >
                        Mehr anzeigen
                    </Button>
                )}
            </div>
        );
    };

    const renderDocumentContent = (): ReactElement => {
        if (isLoading) {
            return <CircularLoader />;
        }

        if (!documents || documents.length === 0 || !visibleDocuments?.length) {
            return <Paragraph>{getTextFromRichTextField(medical_records?.no_documents_found)}</Paragraph>;
        }

        return (
            <>
                {visibleDocuments.map((document) => (
                    <DocumentItem document={document} key={document?.id} downloadDocument={download} />
                ))}
                {filteredDocuments && filteredDocuments.length > 0 && (
                    <div className="mt-4 flex justify-center">{showMoreLessButtons()}</div>
                )}
            </>
        );
    };

    return (
        <div className="flex w-full flex-col gap-4">
            <DocumentCodePopup setVisible={setShowModal} visible={showModal} onInputCodeSuccess={onInputCodeSuccess} />
            {showDocumentsByAppointment && (
                <Paragraph className="text-sm uppercase text-slate-500">Austausch von Dokumenten</Paragraph>
            )}
            <section className="flex flex-col gap-2">{renderDocumentContent()}</section>
        </div>
    );
};

export default DocumentExchange;
